import React, { useState } from 'react';
import { FloatingLabel, Form, Button, Row, Col, Table } from 'react-bootstrap';
import { renderToString } from 'react-dom/server';
import { calculateLoanDetails, generateAmortizationSchedule, currencySymbols, exportAmortizationReportAsPDF } from '../services/LoanCalculatorUtils'; // Assume these utility functions are implemented
import CopyButton from './CopyButton';
import FeatherIcon from 'feather-icons-react';

function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [totalPayment, setTotalPayment] = useState('');
  const [totalInterest, setTotalInterest] = useState('');
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);

  const handleCalculate = () => {
    const { monthlyPayment, totalPayment, totalInterest } = calculateLoanDetails(loanAmount, interestRate, loanTerm);
    const schedule = generateAmortizationSchedule(loanAmount, interestRate, loanTerm);

    setMonthlyPayment(monthlyPayment);
    setTotalPayment(totalPayment);
    setTotalInterest(totalInterest);
    setAmortizationSchedule(schedule);
  };

  const handleReset = () => {
    setLoanAmount('');
    setInterestRate('');
    setLoanTerm('');
    setCurrency('USD');
    setMonthlyPayment('');
    setTotalPayment('');
    setTotalInterest('');
    setAmortizationSchedule([]);
  };

  const handleDownloadPDF = async () => {
    if (!amortizationSchedule || amortizationSchedule.length === 0) {
      alert('Amortization schedule is empty. Please calculate the loan details first.');
      return;
    }

    const reportData = {
      loanAmount,
      interestRate,
      loanTerm,
      currency,
      schedule: amortizationSchedule
    };

    const date = new Date().toLocaleDateString('en-GB').split('/').reverse().join('-');
    const fileName = `AmortizationSchedule-DoltLabsDailyTools-${date}.pdf`;
    await exportAmortizationReportAsPDF(reportData, fileName);
  };

  return (
    <div>
      <Form>
        <Row className='mt-3 mb-3'>
          <Col xs={12} md={6}>
            <FloatingLabel controlId="floatingLoanAmount" label="Loan Amount">
              <Form.Control
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                placeholder="Enter loan amount"
              />
            </FloatingLabel>
          </Col>
          <Col xs={12} md={6}>
            <FloatingLabel controlId="floatingInterestRate" label="Annual Interest Rate (%)">
              <Form.Control
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                placeholder="Enter annual interest rate"
              />
            </FloatingLabel>
          </Col>
        </Row>

        <Row className='mt-3 mb-3'>
          <Col xs={12} md={6}>
            <FloatingLabel controlId="floatingLoanTerm" label="Loan Term (years)">
              <Form.Control
                type="number"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
                placeholder="Enter loan term in years"
              />
            </FloatingLabel>
          </Col>
          <Col xs={12} md={6}>
            <FloatingLabel controlId="floatingCurrency" label="Currency">
              <Form.Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {Object.entries(currencySymbols).map(([code, symbol]) => (
                  <option key={code} value={code}>
                    {code} ({symbol})
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
        </Row>

        <Button variant='dark' className='mt-3 me-2' onClick={handleCalculate}>Calculate</Button>
        <Button variant='outline-dark' className='mt-3' onClick={handleReset}>Reset</Button>
      </Form>

      <h5 className='my-3'>Results</h5>

      <Row className='mt-3 mb-3'>
        <Col xs={12} md={4}>
          <FloatingLabel controlId="floatingMonthlyPayment" label="Monthly Payment">
            <Form.Control
              readOnly
              type="text"
              value={monthlyPayment}
              placeholder="Monthly Payment"
            />
          </FloatingLabel>
        </Col>
        <Col xs={12} md={4}>
          <FloatingLabel controlId="floatingTotalPayment" label="Total Payment (Principal + Interest)">
            <Form.Control
              readOnly
              type="text"
              value={totalPayment}
              placeholder="Total Payment"
            />
          </FloatingLabel>
        </Col>
        <Col xs={12} md={4}>
          <FloatingLabel controlId="floatingTotalInterest" label="Total Interest Paid">
            <Form.Control
              readOnly
              type="text"
              value={totalInterest}
              placeholder="Total Interest Paid"
            />
          </FloatingLabel>
        </Col>
      </Row>

      <h6 className='mt-3'>Amortization Schedule</h6>
      <div className='mt-3 mb-3'>
        <Table responsive bordered>
          <thead>
            <tr>
              <th>Month</th>
              <th>Payment</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {amortizationSchedule.map((row, index) => (
              <tr key={index}>
                <td>{row.month}</td>
                <td>{row.payment}</td>
                <td>{row.principal}</td>
                <td>{row.interest}</td>
                <td>{row.balance}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Button variant='dark' size='sm' className='mt-2 me-2' onClick={handleDownloadPDF}>
        Download Schedule as PDF <FeatherIcon icon='download' size={16} />
      </Button>
      <CopyButton inputText={JSON.stringify(amortizationSchedule)} />
    </div>
  );
}

export default LoanCalculator;